import type { ErrorEvent, LngLatBoundsLike, LngLatLike, Map as MapLibreMap, MapOptions, PaddingOptions, PropertyValueSpecification } from 'maplibre-gl'

import config from '@app/config'

import { onceDiscard } from '@app/utils/onceDiscard'

import type { MapBounds } from '@app/packages/geo/MapBounds'
import type { Point } from '@app/packages/geo/Point'
import { clampLatitude } from '@app/packages/geo/Point'

import classes from './Map.module.scss'

export type MapLibre = typeof import('maplibre-gl')

export type MapMargin = number | [top: number, right: number, bottom: number, left: number]

export const MAP_MIN_ZOOM = 2
export const MAP_MAX_ZOOM = 19
export const MAP_FIT_MAX_ZOOM = 16
export const DEFAULT_MAP_MARGIN = 10
/** Share of the visible size that is loaded beyond each edge, so panning shows tiles instead of blank borders. */
export const OVERSCAN = 0.2

/** MapLibre draws 512px tiles, so its zoom is one below the 256px-tile scale the zoom constants were picked in. */
export const tileZoom = (zoom: number) => zoom - 1

export const loadMapLibre = onceDiscard(async discard => {
  try {
    const [lib, { Protocol }, { default: workerUrl }] = await Promise.all([
      import('maplibre-gl'),
      import('pmtiles'),
      // A bundled MapLibre cannot find its worker from `import.meta.url`; the bundler hands over its url.
      import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'),
      import('maplibre-gl/dist/maplibre-gl.css'),
    ])
    lib.setWorkerUrl(workerUrl)
    lib.addProtocol('pmtiles', new Protocol().tile)
    return lib
  } catch (e) {
    discard()
    throw e
  }
})

export function toLibBounds(bounds: MapBounds): LngLatBoundsLike {
  return [
    [bounds[0][1], bounds[0][0]],
    [bounds[1][1], bounds[1][0]],
  ]
}

export function fromLibBounds(bounds: { getSouthWest(): { lat: number; lng: number }; getNorthEast(): { lat: number; lng: number } }): MapBounds {
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  return [
    [sw.lat, sw.lng],
    [ne.lat, ne.lng],
  ]
}

export type Overscan = { x: number; y: number }

/** Pixels of the map element that lie outside what the visitor sees. */
export function overscanPixels(map: Pick<MapLibreMap, 'getContainer'>): Overscan {
  const host = map.getContainer()
  return { x: (host.clientWidth / (1 + 2 * OVERSCAN)) * OVERSCAN, y: (host.clientHeight / (1 + 2 * OVERSCAN)) * OVERSCAN }
}

/** The visible part of the map element, which is what every size and bound the app reasons about means. */
export function visibleSize(map: Pick<MapLibreMap, 'getContainer'>): { width: number; height: number } {
  const host = map.getContainer()
  return { width: host.clientWidth / (1 + 2 * OVERSCAN), height: host.clientHeight / (1 + 2 * OVERSCAN) }
}

/** The extra pixels keep a fit inside the visible part rather than the whole, larger, element. */
export function toPadding(margin: MapMargin = DEFAULT_MAP_MARGIN, extra: Overscan = { x: 0, y: 0 }): PaddingOptions {
  const [top, right, bottom, left] = typeof margin === 'number' ? [margin, margin, margin, margin] : margin
  return { top: top + extra.y, right: right + extra.x, bottom: bottom + extra.y, left: left + extra.x }
}

export const fitPadding = (map: Pick<MapLibreMap, 'getContainer'>, margin?: MapMargin): PaddingOptions => toPadding(margin, overscanPixels(map))

export function getVisibleBounds(map: Pick<MapLibreMap, 'getContainer' | 'unproject'>): MapBounds {
  const host = map.getContainer()
  const { x, y } = overscanPixels(map)
  const southWest = map.unproject([x, host.clientHeight - y])
  const northEast = map.unproject([host.clientWidth - x, y])
  return [
    [southWest.lat, southWest.lng],
    [northEast.lat, northEast.lng],
  ]
}

export type CreateMapOptions = Omit<MapOptions, 'container' | 'style'> & { margin?: MapMargin }

/** Resolves once the style is in; rejects when the style itself can't be loaded, but not on a single missing tile. */
export async function createMap(container: HTMLElement, { margin, ...options }: CreateMapOptions): Promise<MapLibreMap> {
  const maplibregl = await loadMapLibre()
  // MapLibre's own stylesheet positions its element, which would fight the layout of whatever the caller put the map in.
  // The element is larger than the box it shows through, so panning finds tiles already loaded.
  if (['static', ''].includes(getComputedStyle(container).position)) container.style.position = 'relative'
  container.style.overflow = 'hidden'
  const host = document.createElement('div')
  host.className = classes.map_host
  host.style.setProperty('--map-overscan', String(OVERSCAN))
  container.append(host)
  const { minZoom, maxZoom, ...rest } = options
  const map = new maplibregl.Map({
    attributionControl: { compact: true },
    maxTileCacheSize: TILE_CACHE_SIZE,
    fadeDuration: TILE_FADE_MS,
    fitBoundsOptions: { padding: toPadding(margin, { x: container.clientWidth * OVERSCAN, y: container.clientHeight * OVERSCAN }), maxZoom: MAP_FIT_MAX_ZOOM },
    ...rest,
    minZoom: minZoom ?? MAP_MIN_ZOOM,
    maxZoom: maxZoom ?? MAP_MAX_ZOOM,
    pitchWithRotate: false,
    dragRotate: false,
    touchPitch: false,
    maxPitch: 0,
    container: host,
    style: config.mapsStyleUrl,
  })
  map.keyboard.disableRotation()
  map.touchZoomRotate.disableRotation()
  void map.once('remove', () => host.remove())
  followContainerSize(map, container)

  return new Promise<MapLibreMap>((resolve, reject) => {
    // A source or tile error carries `sourceId` / `tile` on the event, which MapLibre leaves out of the type.
    const onError = (e: ErrorEvent & { sourceId?: string; tile?: unknown }) => {
      if (e.sourceId || e.tile) return
      map.off('error', onError)
      map.remove()
      reject(e.error)
    }
    map.on('error', onError)
    void map.once('load', () => {
      map.off('error', onError)
      rememberBaseLayers(map)
      collapseAttribution(map)
      resolve(map)
    })
  })
}

export type FlightCamera = { center?: LngLatLike; zoom?: number; bearing?: number }

/** MapLibre's own flight, its path and pace, but never longer than `maxMs`. */
export function flyWithin(map: Pick<MapLibreMap, 'flyTo' | 'getContainer' | 'getZoom' | 'getCenter' | 'project'>, camera: FlightCamera, maxMs: number) {
  map.flyTo({ ...camera, duration: Math.min(maxMs, flightDuration(map, camera)) })
}

/** Called once the style is in: every layer there now is the style's, every one after is the app's. */
export function rememberBaseLayers(map: Pick<MapLibreMap, 'getStyle'>) {
  baseLayers.set(map, new Set(map.getStyle().layers.map(layer => layer.id)))
}

/** The lowest layer the app added over the style, which a layer meant to lie under all of ours goes before. */
export function firstOwnLayerId(map: Pick<MapLibreMap, 'getStyle'>): string | undefined {
  const base = baseLayers.get(map)
  if (!base) return undefined
  return map.getStyle().layers.find(layer => !base.has(layer.id))?.id
}

export function metersPerPixel(map: MapLibreMap): number {
  const { lat } = map.getCenter()
  return (EARTH_CIRCUMFERENCE_METERS * Math.cos((lat * Math.PI) / 180)) / (TILE_SIZE * 2 ** map.getZoom())
}

/** A circle layer's radius for `radiusMeters` at `center`, but never under `minPixels`, so zooming out does not lose it. */
export function circleRadius(center: Point, radiusMeters: number, minPixels: number): PropertyValueSpecification<number> {
  const pixelsAt = (zoom: number) => (radiusMeters * TILE_SIZE * 2 ** zoom) / (EARTH_CIRCUMFERENCE_METERS * Math.cos((center.lat * Math.PI) / 180))
  // Base-2 interpolation doubles per zoom level, exactly as the map does; below the zoom it meets the floor, both stops are the floor.
  const floorZoom = Math.log2(minPixels / pixelsAt(0))
  if (floorZoom >= MAP_MAX_ZOOM) return minPixels
  if (floorZoom <= 0) return ['interpolate', ['exponential', 2], ['zoom'], 0, pixelsAt(0), MAP_MAX_ZOOM, pixelsAt(MAP_MAX_ZOOM)]
  return ['interpolate', ['exponential', 2], ['zoom'], 0, minPixels, floorZoom, minPixels, MAP_MAX_ZOOM, pixelsAt(MAP_MAX_ZOOM)]
}

export function circleBounds(center: Point, radiusMeters: number): MapBounds {
  const dLat = radiusMeters / METERS_PER_DEGREE
  const dLon = radiusMeters / (METERS_PER_DEGREE * Math.cos((center.lat * Math.PI) / 180))
  return [
    [clampLatitude(center.lat - dLat), center.lon - dLon],
    [clampLatitude(center.lat + dLat), center.lon + dLon],
  ]
}

/** What `flyTo` would take at its defaults: van Wijk and Nuij's path length, as MapLibre computes it, over its speed. */
function flightDuration(map: Pick<MapLibreMap, 'getContainer' | 'getZoom' | 'getCenter' | 'project'>, { center, zoom }: FlightCamera): number {
  const host = map.getContainer()
  const w0 = Math.max(host.clientWidth, host.clientHeight)
  const w1 = w0 / 2 ** ((zoom ?? map.getZoom()) - map.getZoom())
  const from = map.project(map.getCenter())
  const to = map.project(center ?? map.getCenter())
  const u1 = Math.hypot(to.x - from.x, to.y - from.y)
  const rho2 = FLY_CURVE * FLY_CURVE
  const zoomOutFactor = (descent: boolean) => {
    const b = (w1 * w1 - w0 * w0 + (descent ? -1 : 1) * rho2 * rho2 * u1 * u1) / (2 * (descent ? w1 : w0) * rho2 * u1)
    return Math.log(Math.sqrt(b * b + 1) - b)
  }
  const path = (zoomOutFactor(true) - zoomOutFactor(false)) / FLY_CURVE
  const length = u1 < 0.000002 || !Number.isFinite(path) ? Math.abs(Math.log(w1 / w0)) / FLY_CURVE : path
  return (1000 * length) / FLY_SPEED
}

/** The compact attribution opens itself on load and on every resize; it should wait for a click on the ⓘ. */
function collapseAttribution(map: MapLibreMap) {
  const collapse = () => map.getContainer().querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show')
  collapse()
  map.on('resize', collapse)
}

/** MapLibre only watches the window; a modal opens before it has a width, and a card grows after the map is built. */
const followContainerSize = (map: MapLibreMap, container: HTMLElement) => {
  if (typeof ResizeObserver === 'undefined') return
  const observer = new ResizeObserver(() => map.resize())
  observer.observe(container)
  void map.once('remove', () => observer.disconnect())
}

const baseLayers = new WeakMap<Pick<MapLibreMap, 'getStyle'>, Set<string>>()

const METERS_PER_DEGREE = 111320

// Room for the tiles of the overscanned margin, which a pan brings into view.
const TILE_CACHE_SIZE = 400

const TILE_FADE_MS = 150

const TILE_SIZE = 512

// flyTo's defaults.
const FLY_SPEED = 1.2
const FLY_CURVE = 1.42

const EARTH_CIRCUMFERENCE_METERS = 40075016.686
